import { useState } from "react";
import type { ComparisonTypes } from "../types/ComparisonTypes";
import "../styles/InsuranceComparison.css";
import "../styles/InsuranceInfo.css";

interface InsuranceDetailsProps {
	insurance: ComparisonTypes;
	selectedCategory?: string; // Optional parameter to filter by category
}

const InsuranceDetails = ({
	insurance,
	selectedCategory = "all",
}: InsuranceDetailsProps) => {
	const [expandedSections, setExpandedSections] = useState<Set<string>>(
		new Set(),
	);

	const toggleSection = (section: string) => {
		const newExpandedSections = new Set(expandedSections);
		if (newExpandedSections.has(section)) {
			newExpandedSections.delete(section);
		} else {
			newExpandedSections.add(section);
		}
		setExpandedSections(newExpandedSections);
	};

	// Helper function to display Yes/No instead of boolean values with CSS styling
	const booleanToYesNo = (value: boolean | null | undefined) => {
		if (value === true) return <span className="yes-value">Ja</span>;
		if (value === false) return <span className="no-value">Nei</span>;
		return <span className="unknown-value">Ikke oppgitt</span>;
	};

	const oppositeBooleanToYesNo = (value: boolean | null | undefined) => {
		if (value === true) return <span className="no-value">Ja</span>;
		if (value === false) return <span className="yes-value">Nei</span>;
		return <span className="unknown-value">Ikke oppgitt</span>;
	};

	// Helper function to display either a value or 'Not specified'
	const formatValue = (value: string | number | null | undefined) => {
		if (value === null || value === undefined || value === "") {
			return "Ikke oppgitt";
		}
		return value;
	};

	// Helper function to render a section with consistent styling and accessibility
	const renderSection = (
		key: string,
		title: string,
		renderContent: () => React.ReactNode,
	) => {
		const isExpanded = expandedSections.has(key);
		return (
			<div className="insurance-section" key={key}>
				<button
					className="section-header"
					onClick={() => toggleSection(key)}
					aria-expanded={isExpanded}
					type="button"
				>
					{title} {isExpanded ? "▼" : "▶"}
				</button>
				{isExpanded && <div className="section-details">{renderContent()}</div>}
			</div>
		);
	};

	// Helper function to check if a section should be displayed based on the selected category
	const shouldDisplaySection = (
		sectionKey: string,
		_title?: string,
		_renderFn?: () => React.ReactNode,
	): boolean => {
		if (selectedCategory === "all") return true;
		return sectionKey === selectedCategory;
	};

	return (
		<div className="insurance-details">
			{selectedCategory !== "all" && (
				<div className="filtered-category-info">
					<p>
						Viser kun detaljer for: <strong>{selectedCategory}</strong>
					</p>
				</div>
			)}
			<div className="sections-container">
				{/* Maskinskade Section */}
				{shouldDisplaySection("maskinskade") &&
					renderSection("maskinskade", "Maskinskade", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.maskinskade.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.maskinskade.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.maskinskade.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.maskinskade.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.maskinskade.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.maskinskade.bonus.bonustap)}
							</p>
							<p>
								<strong>Maks alder (år):</strong>{" "}
								{formatValue(insurance.maskinskade.maks_alder_år)}
							</p>
							<p>
								<strong>Maks kjørelengde (km):</strong>{" "}
								{formatValue(insurance.maskinskade.maks_kjorelengde_km)}
							</p>
							<p>
								<strong>Batteri dekket:</strong>{" "}
								{booleanToYesNo(insurance.maskinskade.batteri)}
							</p>

							{insurance.maskinskade.egenandeler &&
								insurance.maskinskade.egenandeler.length > 0 && (
									<div>
										<h4>Egenandeler</h4>
										<ul>
											{insurance.maskinskade.egenandeler.length === 1 && insurance.maskinskade.egenandeler[0].inntil_kjorelengde_km === null
												? <li key={`maskin-egenandel-${insurance.maskinskade.egenandeler[0].egenandel}`}>
													{insurance.maskinskade.egenandeler[0].egenandel}
												  </li>
												: insurance.maskinskade.egenandeler.map((egenandel) => (
													<li key={`maskin-egenandel-${egenandel.inntil_kjorelengde_km}-${egenandel.egenandel}`}>
														{egenandel.inntil_kjorelengde_km !== null 
															? `Inntil ${egenandel.inntil_kjorelengde_km} km: `
															: `Ellers: `}
														{egenandel.egenandel}
													</li>
												))}
										</ul>
									</div>
								)}
						</>
					))}

				{/* Glasskade Section */}
				{shouldDisplaySection("glasskade") &&
					renderSection("glasskade", "Glasskade", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.glasskade.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.glasskade.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.glasskade.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.glasskade.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.glasskade.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.glasskade.bonus.bonustap)}
							</p>
							<p>
								<strong>Glasstak dekket:</strong>{" "}
								{booleanToYesNo(insurance.glasskade.glasstak)}
							</p>

							<h4>Reparasjon av glass</h4>
							<p>
								<strong>Dekket:</strong>{" "}
								{booleanToYesNo(insurance.glasskade.reparasjon_av_glass.dekket)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{insurance.glasskade.reparasjon_av_glass.egenandel}
							</p>
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.glasskade.reparasjon_av_glass.beskrivelse}
							</p>

							<h4>Bytte av glass</h4>
							<p>
								<strong>Dekket:</strong>{" "}
								{booleanToYesNo(insurance.glasskade.bytte_av_glass.dekket)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{insurance.glasskade.bytte_av_glass.egenandel}
							</p>
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.glasskade.bytte_av_glass.beskrivelse}
							</p>
						</>
					))}

				{/* Parkeringsskade Section */}
				{shouldDisplaySection("parkeringsskade") &&
					renderSection("parkeringsskade", "Parkeringsskade", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.parkeringsskade.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.parkeringsskade.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.parkeringsskade.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.parkeringsskade.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.parkeringsskade.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.parkeringsskade.bonus.bonustap,
								)}
							</p>
							<p>
								<strong>Maks alder (år):</strong>{" "}
								{formatValue(insurance.parkeringsskade.maks_alder)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.parkeringsskade.maks_beløp)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.parkeringsskade.egenandel)}
							</p>
						</>
					))}

				{/* Kollisjon Section */}
				{shouldDisplaySection("kollisjon") &&
					renderSection("kollisjon", "Kollisjon", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.kollisjon.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.kollisjon.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.kollisjon.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.kollisjon.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.kollisjon.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.kollisjon.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.kollisjon.egenandel)}
							</p>
							<p>
								<strong>Egenandel fører under 23 år:</strong>{" "}
								{formatValue(insurance.kollisjon.egenandel_fører_under_23)}
							</p>

							<h4>Nybilgaranti</h4>
							<p>
								<strong>Dekket:</strong>{" "}
								{booleanToYesNo(insurance.kollisjon.nybilgaranti.dekket)}
							</p>
							<p>
								<strong>Maks alder (år):</strong>{" "}
								{formatValue(insurance.kollisjon.nybilgaranti.maks_alder)}
							</p>
							<p>
								<strong>Maks kjørelengde (km):</strong>{" "}
								{formatValue(
									insurance.kollisjon.nybilgaranti.maks_kjorelengde_km,
								)}
							</p>
						</>
					))}

				{/* Leiebil Section */}
				{shouldDisplaySection("leiebil") &&
					renderSection("leiebil", "Leiebil", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.leiebil.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.leiebil.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.leiebil.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.leiebil.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.leiebil.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.leiebil.bonus.bonustap)}
							</p>
							<p>
								<strong>Geografisk område:</strong>{" "}
								{formatValue(insurance.leiebil.geografisk_område)}
							</p>

							<h4>Ved benyttet leiebil</h4>
							<p>
								<strong>Maks antall dager:</strong>{" "}
								{formatValue(insurance.leiebil.benyttet.maks_dager)}
							</p>
							<p>
								<strong>Sum dekket (kr/dag):</strong>{" "}
								{formatValue(insurance.leiebil.benyttet.sum_dekket)}
							</p>

							<h4>Ved ikke benyttet leiebil</h4>
							<p>
								<strong>Maks antall dager:</strong>{" "}
								{formatValue(insurance.leiebil.ikke_benyttet.maks_dager)}
							</p>
							<p>
								<strong>Sum dekket (kr/dag):</strong>{" "}
								{formatValue(insurance.leiebil.ikke_benyttet.sum_dekket)}
							</p>
						</>
					))}

				{/* Rettshjelp Section */}
				{shouldDisplaySection("rettshjelp") &&
					renderSection("rettshjelp", "Rettshjelp", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.rettshjelp.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.rettshjelp.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.rettshjelp.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.rettshjelp.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.rettshjelp.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.rettshjelp.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.rettshjelp.egenandel)}
							</p>
							<p>
								<strong>Geografisk område:</strong>{" "}
								{formatValue(insurance.rettshjelp.geografisk_område)}
							</p>

							{insurance.rettshjelp.forsikringssummer && (
								<div>
									<h4>Forsikringssummer</h4>
									<ul>
										{insurance.rettshjelp.forsikringssummer.map((sum) => (
											<li
												key={`rettshjelp-sum-${sum.antall_parter}-${sum.forsikringssum}`}
											>
												{sum.antall_parter}: {sum.forsikringssum} kr
											</li>
										))}
									</ul>
								</div>
							)}
						</>
					))}

				{/* Fører og Passasjer Ulykke Section */}
				{shouldDisplaySection("førerOgPassasjerulykke") &&
					renderSection(
						"førerOgPassasjerulykke",
						"Fører og Passasjer Ulykke",
						() => (
							<>
								<p>
									<strong>Dekkes:</strong>{" "}
									{booleanToYesNo(insurance.førerOgPassasjerulykke.dekkes)}
								</p>
								<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.førerOgPassasjerulykke.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.førerOgPassasjerulykke.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.førerOgPassasjerulykke.tillegg.navn_tilleggsdekning)}
								</p>
							)}
								<p>
									<strong>Beskrivelse:</strong>{" "}
									{insurance.førerOgPassasjerulykke.beskrivelse}
								</p>
								<p>
									<strong>Bonustap:</strong>{" "}
									{oppositeBooleanToYesNo(
										insurance.førerOgPassasjerulykke.bonus.bonustap,
									)}
								</p>
								<p>
									<strong>Maks alder fører og passasjer:</strong>{" "}
									{formatValue(
										insurance.førerOgPassasjerulykke
											.maks_alder_fører_og_passasjer,
									)}
								</p>
								<p>
									<strong>Psykisk hjelp:</strong>{" "}
									{formatValue(insurance.førerOgPassasjerulykke.psykisk_hjelp)}
								</p>

								{insurance.førerOgPassasjerulykke
									.dødsfall_forsikringssummer && (
									<div>
										<h4>Dødsfall forsikringssummer</h4>
										<ul>
											{insurance.førerOgPassasjerulykke.dødsfall_forsikringssummer.map(
												(sum) => (
													<li
														key={`dod-sum-${sum.forsikringssum}-${sum.beskrivelse}`}
													>
														{sum.forsikringssum} kr - {sum.beskrivelse}
													</li>
												),
											)}
										</ul>
									</div>
								)}

								{insurance.førerOgPassasjerulykke
									.invaliditet_forsikringssummer && (
									<div>
										<h4>Invaliditet forsikringssummer</h4>
										<ul>
											{insurance.førerOgPassasjerulykke.invaliditet_forsikringssummer.map(
												(sum) => (
													<li
														key={`invaliditet-sum-${sum.forsikringssum}-${sum.beskrivelse}`}
													>
														{sum.forsikringssum} kr - {sum.beskrivelse}
													</li>
												),
											)}
										</ul>
									</div>
								)}
							</>
						),
					)}

				{/* Brann Section */}
				{shouldDisplaySection("brann") &&
					renderSection("brann", "Brann", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.brann.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.brann.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.brann.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.brann.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.brann.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.brann.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.brann.egenandel)}
							</p>
							<p>
								<strong>Erstatningsgrunnlag:</strong>{" "}
								{formatValue(insurance.brann.erstatningsgrunnlag)}
							</p>
							<p>
								<strong>Dekker avskiltet bil:</strong>{" "}
								{booleanToYesNo(insurance.brann.dekker_avskiltet_bil)}
							</p>
							{insurance.brann.unntak && (
								<p>
									<strong>Unntak:</strong> {insurance.brann.unntak.beskrivelse}
								</p>
							)}
						</>
					))}

				{/* Naturskader Section */}
				{shouldDisplaySection("naturskader") &&
					renderSection("naturskader", "Naturskader", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.naturskader.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.naturskader.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.naturskader.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.naturskader.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.naturskader.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.naturskader.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.naturskader.egenandel)}
							</p>
							<p>
								<strong>Geografisk område:</strong>{" "}
								{formatValue(insurance.naturskader.geografisk_område)}
							</p>
							<p>
								<strong>Erstatningsgrunnlag:</strong>{" "}
								{formatValue(insurance.naturskader.erstatningsgrunnlag)}
							</p>

							<h4>Naturkatastrofer</h4>
							<p>
								<strong>Skred:</strong>{" "}
								{booleanToYesNo(insurance.naturskader.naturkatastrofer.skred)}
							</p>
							<p>
								<strong>Storm:</strong>{" "}
								{booleanToYesNo(insurance.naturskader.naturkatastrofer.storm)}
							</p>
							<p>
								<strong>Flom:</strong>{" "}
								{booleanToYesNo(insurance.naturskader.naturkatastrofer.flom)}
							</p>
							<p>
								<strong>Stormflo:</strong>{" "}
								{booleanToYesNo(
									insurance.naturskader.naturkatastrofer.stormflo,
								)}
							</p>
							<p>
								<strong>Jordskjelv:</strong>{" "}
								{booleanToYesNo(
									insurance.naturskader.naturkatastrofer.jordskjelv,
								)}
							</p>
							<p>
								<strong>Vulkanutbrudd:</strong>{" "}
								{booleanToYesNo(
									insurance.naturskader.naturkatastrofer.vulkanutbrudd,
								)}
							</p>
						</>
					))}

				{/* Tyveri Section */}
				{shouldDisplaySection("tyveri") &&
					renderSection("tyveri", "Tyveri", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.tyveri.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.tyveri.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.tyveri.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.tyveri.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.tyveri.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.tyveri.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.tyveri.egenandel)}
							</p>
							<p>
								<strong>Redusert egenandel ved alarm:</strong>{" "}
								{formatValue(
									insurance.tyveri.redusert_egenandel_ved_alarm_mengde,
								)}
							</p>
							<p>
								<strong>Erstatningsgrunnlag:</strong>{" "}
								{formatValue(insurance.tyveri.erstatningsgrunnlag)}
							</p>
							<p>
								<strong>Leiebil:</strong>{" "}
								{booleanToYesNo(insurance.tyveri.leiebil)}
							</p>
						</>
					))}

				{/* Hærverk Section */}
				{shouldDisplaySection("hærverk") &&
					renderSection("hærverk", "Hærverk", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.hærverk.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.hærverk.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.hærverk.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.hærverk.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.hærverk.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.hærverk.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.hærverk.egenandel)}
							</p>
							<p>
								<strong>Dekkes utenom tyveri:</strong>{" "}
								{booleanToYesNo(insurance.hærverk.dekkes_utenom_tyveri)}
							</p>
							<p>
								<strong>Inkluderer glasskade:</strong>{" "}
								{booleanToYesNo(insurance.hærverk.inkluderer_glasskade)}
							</p>
						</>
					))}

				{/* Fastmontert Utstyr Section */}
				{shouldDisplaySection("fastmontertUtstyr") &&
					renderSection("fastmontertUtstyr", "Fastmontert Utstyr", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.fastmontertUtstyr.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.fastmontertUtstyr.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.fastmontertUtstyr.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.fastmontertUtstyr.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.fastmontertUtstyr.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.fastmontertUtstyr.bonus.bonustap,
								)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.fastmontertUtstyr.egenandel)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.fastmontertUtstyr.maks_beløp)}
							</p>
						</>
					))}

				{/* Bagasje og Eiendeler Section */}
				{shouldDisplaySection("bagasjeOgEiendeler") &&
					renderSection("bagasjeOgEiendeler", "Bagasje og Eiendeler", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.bagasjeOgEiendeler.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.bagasjeOgEiendeler.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.bagasjeOgEiendeler.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.bagasjeOgEiendeler.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.bagasjeOgEiendeler.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.bagasjeOgEiendeler.bonus.bonustap,
								)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.bagasjeOgEiendeler.egenandel)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.bagasjeOgEiendeler.maks_beløp)}
							</p>
							<p>
								<strong>Kontanter beløp:</strong>{" "}
								{formatValue(insurance.bagasjeOgEiendeler.kontanter_beløp)}
							</p>
							<p>
								<strong>Smykker beløp:</strong>{" "}
								{formatValue(insurance.bagasjeOgEiendeler.smykker_beløp)}
							</p>
						</>
					))}

				{/* Utforkjøring Section */}
				{shouldDisplaySection("utforkjøring") &&
					renderSection("utforkjøring", "Utforkjøring", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.utforkjøring.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.utforkjøring.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.utforkjøring.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.utforkjøring.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.utforkjøring.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.utforkjøring.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.utforkjøring.egenandel)}
							</p>
							<p>
								<strong>Egenandel fører under 23:</strong>{" "}
								{formatValue(insurance.utforkjøring.egenandel_fører_under_23)}
							</p>
						</>
					))}

				{/* Bilnøkkel Section */}
				{shouldDisplaySection("bilnøkkel") &&
					renderSection("bilnøkkel", "Bilnøkkel", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.bilnøkkel.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.bilnøkkel.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.bilnøkkel.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.bilnøkkel.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.bilnøkkel.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.bilnøkkel.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.bilnøkkel.egenandel)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.bilnøkkel.maks_beløp)}
							</p>
							<p>
								<strong>Maks utbetalinger per år:</strong>{" "}
								{formatValue(insurance.bilnøkkel.maks_utbetalinger_per_år)}
							</p>
							<p>
								<strong>Omprogrammering:</strong>{" "}
								{booleanToYesNo(insurance.bilnøkkel.omprogrammering)}
							</p>
						</>
					))}

				{/* Feilfylling Section */}
				{shouldDisplaySection("feilfylling") &&
					renderSection("feilfylling", "Feilfylling", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.feilfylling.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.feilfylling.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.feilfylling.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.feilfylling.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong>{" "}
								{insurance.feilfylling.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.feilfylling.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.feilfylling.egenandel)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.feilfylling.maks_beløp)}
							</p>
						</>
					))}

				{/* Veihjelp Section */}
				{shouldDisplaySection("veihjelp") &&
					renderSection("veihjelp", "Veihjelp", () => (
						<>
							<p>
								<strong>Dekkes:</strong>{" "}
								{booleanToYesNo(insurance.veihjelp.dekkes)}
							</p>
							<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.veihjelp.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.veihjelp.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.veihjelp.tillegg.navn_tilleggsdekning)}
								</p>
							)}
							<p>
								<strong>Beskrivelse:</strong> {insurance.veihjelp.beskrivelse}
							</p>
							<p>
								<strong>Bonustap:</strong>{" "}
								{oppositeBooleanToYesNo(insurance.veihjelp.bonus.bonustap)}
							</p>
							<p>
								<strong>Egenandel:</strong>{" "}
								{formatValue(insurance.veihjelp.egenandel)}
							</p>
							<p>
								<strong>Maks beløp:</strong>{" "}
								{formatValue(insurance.veihjelp.maks_beløp)}
							</p>
							<p>
								<strong>Geografisk område:</strong>{" "}
								{formatValue(insurance.veihjelp.geografisk_område)}
							</p>
						</>
					))}

				{/* LadekabelOgElbilUtstyr Section */}
				{shouldDisplaySection("ladekabelOgElbilUtstyr") &&
					renderSection(
						"ladekabelOgElbilUtstyr",
						"Ladekabel og Elbil Utstyr",
						() => (
							<>
								<p>
									<strong>Dekkes:</strong>{" "}
									{booleanToYesNo(insurance.ladekabelOgElbilUtstyr.dekkes)}
								</p>
								<p>
								<strong>Krever tilleggsdekning:</strong>{" "}
								{oppositeBooleanToYesNo(
									insurance.ladekabelOgElbilUtstyr.tillegg.krever_tilleggsdekning,
								)}
							</p>
							{insurance.ladekabelOgElbilUtstyr.tillegg.krever_tilleggsdekning && (
								<p>
									<strong>Navn på tilleggsdekning:</strong>{" "}
									{formatValue(insurance.ladekabelOgElbilUtstyr.tillegg.navn_tilleggsdekning)}
								</p>
							)}
								<p>
									<strong>Beskrivelse:</strong>{" "}
									{insurance.ladekabelOgElbilUtstyr.beskrivelse}
								</p>
								<p>
									<strong>Bonustap:</strong>{" "}
									{oppositeBooleanToYesNo(
										insurance.ladekabelOgElbilUtstyr.bonus.bonustap,
									)}
								</p>
								<p>
									<strong>Egenandel:</strong>{" "}
									{formatValue(insurance.ladekabelOgElbilUtstyr.egenandel)}
								</p>
								<p>
									<strong>Maks beløp:</strong>{" "}
									{formatValue(insurance.ladekabelOgElbilUtstyr.maks_beløp)}
								</p>
								<p>
									<strong>Dekker ladestasjon:</strong>{" "}
									{booleanToYesNo(
										insurance.ladekabelOgElbilUtstyr.dekker_ladestasjon,
									)}
								</p>
							</>
						),
					)}
			</div>
		</div>
	);
};

export default InsuranceDetails;
